import { Box } from '@mui/material';
import { BarChartProps } from '@mui/x-charts';
import React, { useMemo } from 'react';

import BarChart from 'components/charts/bar-chart';
import ChartLegend from 'components/charts/chart-legend';
import { SERIES_HIGHLIGHT } from 'components/charts/constants/chart-defaults';
import { useChartLegendVisibility } from 'components/charts/hooks/use-chart-legend-visibility';
import { useChartAnimation } from 'components/charts/hooks/use-chart-animation';
import { useLegendHighlight } from 'components/charts/hooks/use-legend-highlight';
import { useSeriesVisibility } from 'components/charts/hooks/use-series-visibility';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import DataLabChartHiddenSeriesOverlay from 'views/playground/features/data-lab/components/data-lab-chart-hidden-series-overlay';
import DataLabChartLegendVisibilityMenu from 'views/playground/features/data-lab/components/data-lab-chart-legend-visibility-menu';
import DataLabChartTooltip from 'views/playground/features/data-lab/components/data-lab-chart-tooltip';
import { SelectableChartProps } from 'views/playground/features/data-lab/types/selectable-chart.type';
import { CartesianData } from 'views/playground/features/data-lab/types/chart-series.type';
import { useMetricFormatters } from 'views/playground/features/data-lab/hooks/use-metric-formatters';

type Props = SelectableChartProps &
  Pick<BarChartProps, 'height' | 'skipAnimation'> & {
    data: CartesianData;
    stacked?: boolean;
  };

const CategoryBarChart: React.FC<Props> = ({ data, height = 320, skipAnimation, selectedId, onSelect, stacked }) => {
  const { formatKg } = useMetricFormatters();
  const palette = useChartPalette();
  const chartSkipAnimation = useChartAnimation(skipAnimation);
  const { visible: legendVisible, toggle: toggleLegend } = useChartLegendVisibility();

  const coloredSeries = useMemo(
    () =>
      data.series.map((series, index) => ({
        ...series,
        color: palette.series.categorical[index % palette.series.categorical.length],
      })),
    [data.series, palette.series.categorical]
  );

  const { hiddenIds, toggle, visibleSeries } = useSeriesVisibility(coloredSeries);
  const { highlightedItem, onHighlightChange, onLegendItemHover, onLegendItemLeave } = useLegendHighlight();
  // The card cannot see per-series visibility, so this chart owns the
  // all-series-hidden message.
  const allSeriesHidden = coloredSeries.length > 0 && visibleSeries.length === 0;

  // Keep the y-axis domain stable as series are hidden. Stacked bars use each
  // category's total rather than an individual series maximum.
  const yAxisMax = useMemo(() => {
    if (coloredSeries.length === 0) return undefined;
    if (stacked) {
      return data.categories.reduce((max, _category, categoryIndex) => {
        const total = coloredSeries.reduce((sum, series) => sum + (series.data[categoryIndex] ?? 0), 0);
        return Math.max(max, total);
      }, 0);
    }
    const values = coloredSeries.flatMap((series) => series.data).filter((value): value is number => value !== null);
    return values.length > 0 ? Math.max(...values) : undefined;
  }, [coloredSeries, stacked, data.categories]);

  return (
    <>
      <Box display="flex" justifyContent="flex-end">
        <DataLabChartLegendVisibilityMenu visible={legendVisible} onToggle={toggleLegend} />
      </Box>
      <BarChart
        height={height}
        skipAnimation={chartSkipAnimation}
        hideLegend
        xAxis={[{ data: data.categories.map((category) => category.label), scaleType: 'band', label: 'Category' }]}
        // An explicit axis keeps the domain stable and supplies this chart's unit.
        yAxis={[{ scaleType: 'linear', min: 0, max: yAxisMax, label: 'Kilograms (kg)' }]}
        series={visibleSeries.map((series) => ({
          ...series,
          stack: stacked ? 'total' : undefined,
          valueFormatter: (value: number | null) => (value === null ? '' : formatKg(value)),
          ...SERIES_HIGHLIGHT,
        }))}
        highlightedItem={highlightedItem}
        onHighlightChange={onHighlightChange}
        onItemClick={(_event, barItemIdentifier) => {
          const category = data.categories[barItemIdentifier.dataIndex];
          if (!category) return;
          onSelect?.(category.id === selectedId ? null : category.id);
        }}
        slots={{ noDataOverlay: DataLabChartHiddenSeriesOverlay, tooltip: DataLabChartTooltip }}
        sx={
          allSeriesHidden
            ? // Dims axis ticks/labels so the frame reads as inert rather than
              // real data; state-dependent, so it cannot live in the theme.
              { '& text:not(.chart-hidden-series-overlay-text)': { opacity: 0.4 } }
            : undefined
        }
      />
      {legendVisible && (
        <ChartLegend
          items={coloredSeries.map((series) => ({ id: String(series.id), label: series.label, color: series.color }))}
          hiddenIds={hiddenIds}
          onToggle={toggle}
          onItemHover={onLegendItemHover}
          onItemLeave={onLegendItemLeave}
        />
      )}
    </>
  );
};

export default CategoryBarChart;
